import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, AlertCircle, CheckCircle2, XCircle, Clock, ChevronRight, RotateCcw } from 'lucide-react';
import ohipTransactionsService from '../services/OhipTransactionsService';
import Button from './shared/Button';
import Spinner from './shared/Spinner';
import ConfirmModal from './shared/ConfirmModal';
import Toast from './shared/Toast';
import { formatDateTime } from '../utils/datetime';
import './ExtractionRunsPage.css';

// Admin > Billing > Extraction Runs.
//
// The daily OHIP Transactions ingest, execution by execution, read straight from Step Functions. The
// executions ARE the run history — nothing keeps a second copy, so this cannot miss a run, and the run
// that died before it could write its own epitaph is still here.
//
// The statistics come from each execution's OUTPUT, flattened by the same layer module that builds the
// Teams status post, so the screen and the channel can never disagree about what a run did.

const STATUS_FILTERS = [
    { value: 'ALL', label: 'All' },
    { value: 'SUCCEEDED', label: 'Succeeded' },
    { value: 'FAILED', label: 'Failed' },
    { value: 'RUNNING', label: 'Running' },
];

const num = (n) => (n == null ? '—' : Number(n).toLocaleString());

const duration = (ms) => {
    if (ms == null || ms < 0) return '—';
    const s = Math.round(ms / 1000);
    if (s < 60) return `${s}s`;
    return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
};

// A run is not simply green or red. SUCCEEDED with unfetched reports, failed discovery or failed checks is
// a run that worked and lost something — showing it as a clean pass is how a slow degradation goes unseen
// for a fortnight.
const outcomeOf = (r) => {
    if (r.status === 'RUNNING') return 'running';
    if (r.status !== 'SUCCEEDED') return 'failed';
    if (r.fetchDegraded || r.discoverFailed || r.checksFailed || (r.fetch && r.fetch.failed)) return 'degraded';
    return 'ok';
};

const OUTCOME = {
    ok:       { label: 'Succeeded', icon: CheckCircle2, cls: 'exr-ok' },
    degraded: { label: 'Completed with issues', icon: AlertCircle, cls: 'exr-degraded' },
    failed:   { label: 'Failed', icon: XCircle, cls: 'exr-failed' },
    running:  { label: 'Running', icon: Clock, cls: 'exr-running' },
};

function ExtractionRunsPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [status, setStatus] = useState('ALL');
    const [limit, setLimit] = useState(30);
    const [expanded, setExpanded] = useState(null);
    const [confirmRerun, setConfirmRerun] = useState(null);
    const [rerunning, setRerunning] = useState(false);
    const [toast, setToast] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await ohipTransactionsService.runs({ status, limit });
            setData(res.data || null);
        } catch (e) {
            setError(e.response?.data?.message || e.message || 'Failed to load extraction runs');
        } finally {
            setLoading(false);
        }
    }, [status, limit]);

    useEffect(() => { load(); }, [load]);

    // RE-RUN IS SAFE TO PRESS, and the confirm exists to say what it will do rather than to guard against
    // damage: the pipeline is idempotent — reports already in S3 are not re-fetched, documents are written
    // under deterministic ids so nothing is double-counted, and a day that is already complete produces no
    // work at all. What it is NOT safe to do is start a second run alongside a running one, and the
    // endpoint refuses that with a 409 rather than the screen trying to police it.
    const doRerun = async () => {
        const run = confirmRerun;
        setConfirmRerun(null);
        setRerunning(true);
        try {
            const res = await ohipTransactionsService.rerun({ executionArn: run.executionArn });
            setToast({ kind: 'success', message: `Re-run started (${res.data?.name || 'new execution'}). It picks up whatever is missing.` });
            await load();
        } catch (e) {
            // A 409 (already running) is the expected refusal, not a fault — its message says what to do.
            setToast({ kind: 'error', message: e.response?.data?.message || e.message || 'Could not start the re-run' });
        } finally {
            setRerunning(false);
        }
    };

    const totals = data && data.totals;

    return (
        <div className="otx-page">
            <div className="otx-header">
                <div>
                    <h1>Extraction Runs</h1>
                    <p className="otx-subtitle">
                        The daily OHIP Transactions ingest, run by run — what each one planned, fetched,
                        indexed and found. Read live from the Step Function, so nothing is missing.
                    </p>
                </div>
                <div className="otx-header-actions">
                    <Button variant="success" onClick={load}>
                        <RefreshCw className="icon" /> Refresh
                    </Button>
                </div>
            </div>

            <div className="otx-filters">
                <label>
                    <span>Status</span>
                    <select value={status} onChange={e => setStatus(e.target.value)}>
                        {STATUS_FILTERS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                </label>
                <label>
                    <span>Show</span>
                    <select value={limit} onChange={e => setLimit(Number(e.target.value))}>
                        {[10, 30, 60].map(n => <option key={n} value={n}>Last {n} runs</option>)}
                    </select>
                </label>
            </div>

            {error && <div className="otx-error"><AlertCircle size={16} /> {error}</div>}

            {/* Not an empty table: an unconfigured ARN and a pipeline that has never run look identical
                otherwise, and only one of them is something to go and fix. */}
            {data && data.configured === false && (
                <div className="otx-error"><AlertCircle size={16} /> {data.message}</div>
            )}

            {loading && !data ? <Spinner /> : totals && (
                <div className="otx-stats">
                    <div className="otx-stat">
                        <span className="otx-stat-label">Last run</span>
                        <span className="otx-stat-value">
                            {totals.lastRunAt ? formatDateTime(totals.lastRunAt) : '—'}
                        </span>
                    </div>
                    <div className="otx-stat">
                        <span className="otx-stat-label">Succeeded</span>
                        <span className="otx-stat-value otx-ok">{num(totals.succeeded)}</span>
                    </div>
                    <div className="otx-stat">
                        <span className="otx-stat-label">Failed</span>
                        <span className={`otx-stat-value ${totals.failed ? 'otx-bad' : ''}`}>{num(totals.failed)}</span>
                    </div>
                    <div className="otx-stat">
                        <span className="otx-stat-label">With issues</span>
                        <span className={`otx-stat-value ${totals.degraded ? 'exr-warn-text' : ''}`}>{num(totals.degraded)}</span>
                    </div>
                    <div className="otx-stat">
                        <span className="otx-stat-label">Documents indexed</span>
                        <span className="otx-stat-value">{num(totals.documents)}</span>
                    </div>
                    <div className="otx-stat">
                        <span className="otx-stat-label">Median duration</span>
                        <span className="otx-stat-value">{duration(totals.medianDurationMs)}</span>
                    </div>
                </div>
            )}

            <div className="otx-table-wrap">
                <table className="otx-table exr-table">
                    <thead>
                        <tr>
                            <th style={{ width: 34 }} />
                            <th>Started</th>
                            <th>Outcome</th>
                            <th>Days</th>
                            <th className="otx-numeric">Environments</th>
                            <th className="otx-numeric">Reports</th>
                            <th className="otx-numeric">Requests</th>
                            <th className="otx-numeric">Documents</th>
                            <th>Checks</th>
                            <th className="otx-numeric">Duration</th>
                            <th style={{ width: 96 }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={11}><Spinner /></td></tr>}
                        {!loading && (!data || !data.runs.length) && (
                            <tr><td colSpan={11} className="otx-empty">No extraction runs found.</td></tr>
                        )}
                        {!loading && data && data.runs.map((r) => {
                            const o = OUTCOME[outcomeOf(r)];
                            const Icon = o.icon;
                            const open = expanded === r.executionArn;
                            return (
                                <React.Fragment key={r.executionArn || r.name}>
                                    <tr className={`exr-row ${open ? 'exr-row-open' : ''}`}
                                        onClick={() => setExpanded(open ? null : r.executionArn)}>
                                        <td className="exr-chevron">
                                            <ChevronRight size={15} className={open ? 'exr-chevron-open' : ''} />
                                        </td>
                                        <td>
                                            {r.startedAt ? formatDateTime(r.startedAt) : '—'}
                                            {r.trigger === 'manual' && <span className="exr-tag">manual</span>}
                                        </td>
                                        <td>
                                            <span className={`exr-outcome ${o.cls}`}><Icon size={14} /> {o.label}</span>
                                        </td>
                                        <td>
                                            {r.window ? `${r.window.from} → ${r.window.to}` : (r.dates || []).join(', ') || '—'}
                                        </td>
                                        <td className="otx-numeric">{num(r.environmentsPlanned)}</td>
                                        <td className="otx-numeric">
                                            {r.fetch ? `${num(r.fetch.fetched)}/${num(r.fetch.attempted)}` : '—'}
                                            {r.fetch && r.fetch.failed > 0 && (
                                                <span className="exr-mini exr-warn-text"> {r.fetch.failed} failed</span>
                                            )}
                                        </td>
                                        <td className="otx-numeric">{r.fetch ? num(r.fetch.totalRequests) : '—'}</td>
                                        <td className="otx-numeric">{r.index ? num(r.index.documents) : '—'}</td>
                                        <td>
                                            {!r.checks && <span className="otx-muted">—</span>}
                                            {r.checks && r.checks.dormancyBreaches === 0 && r.checks.anomalies === 0 && (
                                                <span className="otx-muted">clear</span>
                                            )}
                                            {r.checks && r.checks.dormancyBreaches > 0 && (
                                                <span className="exr-chip exr-chip-warn">{r.checks.dormancyBreaches} dormant</span>
                                            )}
                                            {r.checks && r.checks.anomalies > 0 && (
                                                <span className="exr-chip exr-chip-warn">{r.checks.anomalies} anomaly</span>
                                            )}
                                        </td>
                                        <td className="otx-numeric">{duration(r.durationMs)}</td>
                                        <td>
                                            {/* Offered on anything that did not finish cleanly — a failure
                                                OR a run that completed with unfetched reports, because the
                                                second is the one that quietly leaves a gap in the billing.
                                                Never on a RUNNING one. stopPropagation so the button does
                                                not also toggle the row open. */}
                                            {(outcomeOf(r) === 'failed' || outcomeOf(r) === 'degraded') && (
                                                <button
                                                    className="exr-rerun-btn"
                                                    disabled={rerunning}
                                                    title="Run the ingest again — it only fetches what is missing"
                                                    onClick={(e) => { e.stopPropagation(); setConfirmRerun(r); }}
                                                >
                                                    <RotateCcw size={13} /> Re-run
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                    {open && (
                                        <tr className="exr-detail-row">
                                            <td colSpan={11}>
                                                <div className="exr-detail">
                                                    <div className="exr-detail-grid">
                                                        <div><span>Execution</span><code>{r.name}</code></div>
                                                        <div><span>Trigger</span>{r.trigger}{r.input ? ` — ${JSON.stringify(r.input)}` : ''}</div>
                                                        <div><span>Finished</span>{r.stoppedAt ? formatDateTime(r.stoppedAt) : '—'}</div>
                                                        {r.environmentsSkipped > 0 && (
                                                            <div><span>Environments skipped</span>{r.environmentsSkipped} (no hotel codes)</div>
                                                        )}
                                                        {r.fetch && (
                                                            <div><span>Fetch</span>
                                                                {num(r.fetch.fetched)} fetched, {num(r.fetch.cached)} already in S3,
                                                                {' '}{num(r.fetch.failed)} failed
                                                            </div>
                                                        )}
                                                        {r.index && (
                                                            <div><span>Index</span>
                                                                {num(r.index.documents)} documents, {num(r.index.environments)} environments,
                                                                {' '}{num(r.index.dayCount)} day(s)
                                                            </div>
                                                        )}
                                                        {r.discover && (
                                                            <div><span>Discovery</span>
                                                                {num(r.discover.environments)} environments, {num(r.discover.hotelCodes)} hotel codes
                                                                {r.discover.environmentsCreated > 0 && `, ${r.discover.environmentsCreated} new environment(s)`}
                                                            </div>
                                                        )}
                                                        {r.checks && (
                                                            <div><span>Checks</span>
                                                                {r.checks.dormancyBreaches} dormancy breach(es), {r.checks.anomalies} volume anomal
                                                                {r.checks.anomalies === 1 ? 'y' : 'ies'}
                                                                {r.checks.dormantEnvironments.length > 0 && ` — ${r.checks.dormantEnvironments.join(', ')}`}
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* The caveats a green tick would otherwise hide. */}
                                                    {(r.fetchDegraded || r.discoverFailed || r.checksFailed) && (
                                                        <ul className="exr-caveats">
                                                            {r.fetchDegraded && <li>Some reports could not be fetched — the next run retries only the missing ones.</li>}
                                                            {r.discoverFailed && <li>Environment discovery failed. Billing is unaffected; newly-seen hotels were not added.</li>}
                                                            {r.checksFailed && <li>Post-ingest checks did not run, so dormancy and volume were not evaluated.</li>}
                                                        </ul>
                                                    )}

                                                    {(r.error || r.errorMessage) && (
                                                        <div className="exr-error">
                                                            <strong>{r.error}</strong>
                                                            {r.errorMessage && <div>{r.errorMessage}</div>}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {confirmRerun && (
                <ConfirmModal
                    title="Re-run the extraction"
                    message={
                        `Start the daily ingest again${confirmRerun.window ? ` for ${confirmRerun.window.from} → ${confirmRerun.window.to}` : ''}. `
                        + 'Reports already in S3 are not fetched again and indexed days are rewritten in place, so nothing is '
                        + 'double-counted — it costs only what is missing. It will be refused if a run is already in progress.'
                    }
                    confirmLabel="Re-run"
                    onConfirm={doRerun}
                    onCancel={() => setConfirmRerun(null)}
                />
            )}

            {toast && (
                <Toast message={toast.message} kind={toast.kind} onClose={() => setToast(null)} />
            )}
        </div>
    );
}

export default ExtractionRunsPage;
